import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

const COHORT_CHRONOLOGICAL_ORDER = [
  '30 Jun, 1 July, 3 July 2025',
  '7-9 July 2025',
  '26-28 August 2025',
  '16-18 September 2025',
  '11-13 November 2025',
  '20-21-22 January 2026',
  'FY26Q4 - Self-Paced iLearn',
  'FY27Q1 - Self-Paced iLearn',
  'FY27Q2 - July 20-22',
];

const SHORT_COHORT_LABELS = {
  '30 Jun, 1 July, 3 July 2025': '30 Jun – 3 Jul',
  '7-9 July 2025': '7–9 Jul',
  '26-28 August 2025': '26–28 Aug',
  '16-18 September 2025': '16–18 Sep',
  '11-13 November 2025': '11–13 Nov',
  '20-21-22 January 2026': '20–22 Jan',
  'FY27Q1 - Self-Paced iLearn': 'FY27Q1 Self-Paced',
  'FY26Q4 - Self-Paced iLearn': 'FY26Q4 Self-Paced',
  'FY27Q2 - July 20-22': 'FY27Q2 Jul 20–22',
};

const getShortCohortLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Unknown';
  if (SHORT_COHORT_LABELS[raw]) return SHORT_COHORT_LABELS[raw];
  if (raw.length <= 18) return raw;
  return raw.replace(/\s+\d{4}$/g, '').slice(0, 22).trim();
};

const getCohortSortIndex = (cohort) => {
  const raw = String(cohort || '').trim();
  const orderIndex = COHORT_CHRONOLOGICAL_ORDER.indexOf(raw);
  if (orderIndex >= 0) {
    return orderIndex;
  }

  const normalized = raw.toLowerCase();
  const fuzzyIndex = COHORT_CHRONOLOGICAL_ORDER.findIndex(
    (item) => item.toLowerCase() === normalized || normalized.includes(item.toLowerCase())
  );
  if (fuzzyIndex >= 0) {
    return fuzzyIndex;
  }

  return COHORT_CHRONOLOGICAL_ORDER.length + 1;
};

const sortCohortTrendData = (data = []) =>
  [...data].sort((a, b) => {
    const orderDiff = getCohortSortIndex(a.cohort) - getCohortSortIndex(b.cohort);
    if (orderDiff !== 0) {
      return orderDiff;
    }
    return String(a.cohort || '').localeCompare(String(b.cohort || ''));
  });

const CertificationTrendByCohortChart = ({ data = [] }) => {
  const chartData = useMemo(() => sortCohortTrendData(data), [data]);

  const option = useMemo(
    () => ({
      animationDuration: 280,
      grid: {
        left: 42,
        right: 18,
        top: 20,
        bottom: 62,
        containLabel: false,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#ffffff',
        borderColor: '#d7e4f2',
        borderWidth: 1,
        padding: [10, 12],
        textStyle: {
          color: '#1f3c66',
          fontSize: 12,
        },
        extraCssText: 'box-shadow: 0 10px 24px rgba(14, 53, 94, 0.14); border-radius: 10px;',
        formatter: (params) => {
          const point = Array.isArray(params) ? params[0] : params;
          const item = chartData[point?.dataIndex];
          if (!item) {
            return '';
          }

          return [
            `<strong>${item.cohort}</strong>`,
            `Completion Rate: <strong>${item.completionRate || 0}%</strong>`,
            `Certified: <strong>${item.completed || 0}</strong>`,
            `Total Participants: <strong>${item.total || 0}</strong>`,
          ].join('<br/>');
        },
      },
      xAxis: {
        type: 'category',
        data: chartData.map((item) => getShortCohortLabel(item.cohort)),
        boundaryGap: false,
        axisLine: {
          lineStyle: { color: 'rgba(15, 47, 87, 0.12)' },
        },
        axisTick: { show: false },
        axisLabel: {
          color: '#64748b',
          fontSize: 10,
          fontWeight: 500,
          rotate: chartData.length > 6 ? 32 : 0,
          interval: 0,
          hideOverlap: true,
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        axisLabel: {
          color: '#64748b',
          fontSize: 11,
          formatter: '{value}%',
        },
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: 'rgba(15, 47, 87, 0.08)',
          },
        },
      },
      series: [
        {
          name: 'Completion Rate',
          type: 'line',
          data: chartData.map((item) => item.completionRate || 0),
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: {
            width: 2.5,
            color: '#4b93f3',
          },
          itemStyle: {
            color: '#4b93f3',
            borderColor: '#ffffff',
            borderWidth: 2,
          },
          emphasis: {
            scale: 1.15,
            itemStyle: {
              color: '#1f6fda',
              borderColor: '#ffffff',
              borderWidth: 2,
            },
          },
        },
      ],
    }),
    [chartData]
  );

  if (!chartData.length) {
    return <div className="bootcamp-cohort-trend-empty">No cohort trend data available.</div>;
  }

  return (
    <div className="bootcamp-cohort-trend-chart">
      <ReactECharts option={option} style={{ height: '320px', width: '100%' }} opts={{ renderer: 'canvas' }} />
    </div>
  );
};

export default CertificationTrendByCohortChart;
